import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

export interface BackendHealth {
  state?: string;
  db?: string | null;
  version?: string | null;
  latencyMs?: number | null;
  checkedAt?: Date | null;
  reason?: string | null;
}

const formatCheckedAt = (checkedAt?: Date | null) => {
  if (!checkedAt) return 'never';
  return checkedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const DetailRow = ({ term, children }: { term: string; children: React.ReactNode }) => (
  <>
    <Typography component='dt' variant='caption' color='text.secondary' sx={{ fontWeight: 500 }}>
      {term}
    </Typography>
    <Typography component='dd' variant='caption' sx={{ m: 0 }}>
      {children}
    </Typography>
  </>
);

/**
 * Body of the health indicator's tooltip. Rows are omitted when the backend never
 * answered, since reporting "Database: unknown" is noise rather than information.
 */
export default function HealthDetail({ health }: { health?: BackendHealth }) {
  return (
    <Box sx={{ minWidth: 208 }}>
      {health?.reason && (
        <Typography variant='caption' component='p' sx={{ mb: 1 }}>
          {health.reason}
        </Typography>
      )}
      <Box component='dl' sx={{ display: 'grid', gridTemplateColumns: '5fr 7fr', columnGap: 1, rowGap: 0.25, m: 0 }}>
        {health?.db && <DetailRow term='Database'>{health.db}</DetailRow>}
        {health?.version && <DetailRow term='API version'>{health.version}</DetailRow>}
        {typeof health?.latencyMs === 'number' && <DetailRow term='Response'>{`${health.latencyMs} ms`}</DetailRow>}
        <DetailRow term='Last checked'>{formatCheckedAt(health?.checkedAt)}</DetailRow>
      </Box>
    </Box>
  );
}
